import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "@emailjs/browser";
import type { ContactInfo as ContactInfoType } from "../types";
import {
  Container,
  Section,
  SectionTitle,
  Card,
  Button,
} from "../styles/GlobalStyles";
import { useLanguage } from "../contexts/LanguageContext";

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div`
  h3 {
    font-size: 1.8rem;
    color: #2c3e50;
    margin-bottom: 1.5rem;
  }

  p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  color: #666;

  &:hover {
    background: #f8f9fa;
    color: #667eea;
    transform: translateX(5px);
  }

  .icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .content {
    flex: 1;

    .label {
      font-size: 0.9rem;
      color: #999;
      margin-bottom: 0.2rem;
    }

    .value {
      font-weight: 500;
      color: inherit;
    }
  }
`;

const ContactForm = styled(Card)`
  padding: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #2c3e50;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SubmitButton = styled(Button)`
  align-self: flex-start;
  position: relative;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ $type: "success" | "error" }>`
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
  background: ${(props) => (props.$type === "success" ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.$type === "success" ? "#155724" : "#721c24")};
  border: 1px solid
    ${(props) => (props.$type === "success" ? "#c3e6cb" : "#f5c6cb")};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
  }
`;

interface ContactProps {
  contactInfo: ContactInfoType;
}

const Contact: React.FC<ContactProps> = ({ contactInfo }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus(null);

    try {
      const serviceId = "service_8x7mqy8";
      const templateId = "template_813oflm";
      const publicKey = "xZmrrL6g-fZzqraw6";

      const templateParams = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setStatus({
        type: "success",
        message: t("contact.success"),
      });

      // Réinitialiser le formulaire
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setStatus({
        type: "error",
        message: t("contact.error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section id="contact">
      <Container>
        <SectionTitle>{t("contact.title")}</SectionTitle>

        <ContactContent>
          <ContactInfo>
            <h3>{t("contact.subtitle")}</h3>
            <p>{t("contact.description")}</p>

            <ContactList>
              <ContactItem href={`mailto:${contactInfo.email}`}>
                <div className="icon">✉</div>
                <div className="content">
                  <div className="label">{t("contact.info.email")}</div>
                  <div className="value">{contactInfo.email}</div>
                </div>
              </ContactItem>

              {contactInfo.phone && (
                <ContactItem href={`tel:${contactInfo.phone}`}>
                  <div className="icon">📱</div>
                  <div className="content">
                    <div className="label">{t("contact.info.phone")}</div>
                    <div className="value">{contactInfo.phone}</div>
                  </div>
                </ContactItem>
              )}
            </ContactList>

            <SocialLinks>
              {contactInfo.linkedin && (
                <SocialLink
                  href={contactInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="LinkedIn"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </SocialLink>
              )}

              {contactInfo.github && (
                <SocialLink
                  href={contactInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="GitHub"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                </SocialLink>
              )}
            </SocialLinks>

            <div style={{ marginTop: "2rem" }}>
              <h4 style={{ marginBottom: "1rem", color: "#2c3e50" }}>
                {t("contact.documents")}
              </h4>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <Button
                  as="a"
                  href={`${process.env.PUBLIC_URL}/assets/JOBIT_RESUME.pdf`}
                  download="JOBIT_RESUME.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  style={{ fontSize: "0.9rem", padding: "0.8rem 1.5rem" }}
                >
                  📄 {t("contact.downloadCv")}
                </Button>
              </div>
            </div>
          </ContactInfo>

          <ContactForm>
            <h3 style={{ marginBottom: "1.5rem", color: "#2c3e50" }}>
              {t("contact.sendMessage")}
            </h3>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">{t("contact.form.name")} *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">{t("contact.form.email")} *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">{t("contact.form.subject")} *</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">{t("contact.form.message")} *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <SubmitButton
                type="submit"
                variant="primary"
                disabled={isLoading}
              >
                {isLoading ? t("contact.form.sending") : t("contact.form.send")}
              </SubmitButton>

              {status && (
                <StatusMessage $type={status.type}>
                  {status.message}
                </StatusMessage>
              )}
            </Form>
          </ContactForm>
        </ContactContent>
      </Container>
    </Section>
  );
};

export default Contact;
